import { useCallback, useEffect, useRef, useState } from 'react'

import { useParams } from 'react-router-dom'

import DemoController from '@/components/DemoController/DemoController'
import FrameContainer from '@/components/FrameContainer/FrameContainer'
import IntroTemplate from '@/components/IntroTemplate/IntroTemplate'
import BaseballTemplate from '@/components/ScoreTemplates/Baseball/BaseballTemplate'
import CricketManualTemplate from '@/components/ScoreTemplates/Cricket/Manual/CricketManualTemplate'
import GoalSportTemplate from '@/components/ScoreTemplates/GoalSport/GoalSportTemplate'
import TennisTemplate from '@/components/ScoreTemplates/Tennis/TennisTemplate'
import VolleyballTemplate from '@/components/ScoreTemplates/Volleyball/VolleyballTemplate'
import SponsorBanners from '@/components/SponsorBanners/SponsorBanners'
import { SPORT_ID } from '@/config/sports'
import { useTheme } from '@/contexts/ThemeContext'
import { useAnimatedTransition } from '@/hooks/useAnimatedTransition'
import { useMatchData } from '@/hooks/useMatchData'
import { useQueryParams } from '@/hooks/useQueryParams'
import { parseDisplayState } from '@/services/jsonDataService'
import { isDemo } from '@/services/matchConfigService'
import { fetchMatchScore, setDemoSport } from '@/services/matchScoreService'
import { DISPLAY_STATE, GFX_SLOT_ID } from '@/types'
import './Match.css'

import type { GfxState, ScoreBugPosition } from '@/services/jsonDataService'
import type { JsonMatchData } from '@/services/jsonDataService'
import type { GfxConfig, MatchData } from '@/types'
import type { ComponentType } from 'react'

interface TemplateProps {
  matchData: MatchData
  customerId: string
  eventGuid: string
  jsonData: JsonMatchData | null
  displayState: DISPLAY_STATE
  scoreBugPosition: ScoreBugPosition
  isVisible?: boolean
}

const mapAll = (
  ids: SPORT_ID[],
  template: ComponentType<TemplateProps>
): Record<number, ComponentType<TemplateProps>> => Object.fromEntries(ids.map((id) => [id, template]))

const templateComponents: Record<number, ComponentType<TemplateProps>> = {
  // Team score + clock / periods.
  // AFL rides this too, so it shows a single total per side — goals and
  // behinds are not modelled anywhere in the payload.
  ...mapAll(
    [
      SPORT_ID.SOCCER,
      SPORT_ID.AFL,
      SPORT_ID.BASKETBALL,
      SPORT_ID.FIELD_HOCKEY,
      SPORT_ID.FUTSAL,
      SPORT_ID.NETBALL,
      SPORT_ID.RUGBY_LEAGUE,
      SPORT_ID.RUGBY_UNION,
      SPORT_ID.WATER_POLO,
    ],
    GoalSportTemplate
  ),
  // Set based scorebug / scorebar
  ...mapAll(
    [
      SPORT_ID.VOLLEYBALL,
      SPORT_ID.BEACH_VOLLEYBALL,
      SPORT_ID.BADMINTON,
      SPORT_ID.PADEL,
      SPORT_ID.PICKLEBALL,
      SPORT_ID.SQUASH,
      SPORT_ID.TABLE_TENNIS,
    ],
    VolleyballTemplate
  ),
  ...mapAll([SPORT_ID.BASEBALL, SPORT_ID.SOFTBALL], BaseballTemplate),
  [SPORT_ID.TENNIS]: TennisTemplate,
}

/**
 * Cricket has distinct sport IDs because the UIs live as siblings under
 * ScoreTemplates/Cricket/ and are driven by completely different data:
 *
 *   SPORT_ID.CRICKET_MANUAL (12) → Cricket/Manual/CricketManualTemplate
 *       Operator types free-text scores via FrogBox Go's cricket panel.
 *       Used today.
 *
 *   SPORT_ID.INDOOR_CRICKET (17) → Cricket/Manual/CricketManualTemplate
 *       Same operator-driven panel. Indoor scoring differs (fixed overs,
 *       skins, negative runs) but nothing in the manual bug encodes those
 *       rules, so it renders correctly as-is. Split it out if indoor ever
 *       needs its own layout.
 *
 *   SPORT_ID.CRICKET (1)         → Cricket/Comp/  (placeholder folder)
 *       Comp-provider-driven cricket (live-scores integration). Future
 *       work; mirror the Manual/ structure when implemented. Falls through
 *       to "Unsupported sport ID" until the comp template exists.
 *
 * When comp-cricket lands, register it in `templateComponents` keyed by
 * SPORT_ID.CRICKET — do NOT collapse it into the manual template.
 */
const resolveTemplate = (sportId: number): ComponentType<TemplateProps> | undefined => {
  if (sportId === SPORT_ID.CRICKET_MANUAL || sportId === SPORT_ID.INDOOR_CRICKET) return CricketManualTemplate
  return templateComponents[sportId]
}

interface MatchProps {
  blackout: boolean
  resOverride?: { height: number; width: number }
}

const Match = ({ blackout, resOverride }: MatchProps) => {
  const { customerId, eventGuid } = useParams<{ customerId: string; eventGuid: string }>()
  const { sport: initialSport } = useQueryParams()
  const [s3Data, setS3Data] = useState<JsonMatchData | null>(null)
  const [s3Loading, setS3Loading] = useState(true)
  const [s3Error, setS3Error] = useState<string | null>(null)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [demoPayloadVersion, setDemoPayloadVersion] = useState(0)
  // Mirror the initial sport (from ?sport= query param) into the matchScore
  // service ONCE before the first fetch fires, otherwise it loads the default
  // (volleyball) payload regardless of the URL. Call inside useState initialiser
  // so it runs exactly once at mount; calling on every render would clobber
  // DemoController's per-scenario setDemoPayload writes.
  const [demoSportOverride, setDemoSportOverride] = useState<SPORT_ID | undefined>(() => {
    if (initialSport !== undefined) {
      setDemoSport(initialSport)
    }
    return initialSport
  })
  const [demoDisplayStateOverride, setDemoDisplayStateOverride] = useState<DISPLAY_STATE | undefined>(undefined)
  const [demoScoreBugPosition, setDemoScoreBugPosition] = useState<ScoreBugPosition | undefined>(undefined)
  const [demoGfxState, setDemoGfxState] = useState<GfxState>({})

  if (!customerId || !eventGuid) {
    throw new Error('Missing customerId or eventGuid parameters')
  }

  const { data: matchData, isLoading, error } = useMatchData(customerId, eventGuid)
  const theme = useTheme()
  const isDemoMode = isDemo(customerId)

  // Default blackout to true for demo mode (provides visible background)
  const effectiveBlackout = isDemoMode ? true : blackout
  const backgroundColor = effectiveBlackout ? theme.colors.blackout : 'transparent'

  // Callback for when demo payload changes - triggers refetch
  const handleDemoPayloadChange = useCallback(() => {
    setDemoPayloadVersion((v) => v + 1)
  }, [])

  // Callback for when demo sport changes
  const handleDemoSportChange = useCallback((newSportId: SPORT_ID) => {
    setDemoSportOverride(newSportId)
    // Point the fetcher at the new sport's default demo payload BEFORE bumping
    // the version, otherwise the next refetch hits the previous sport's data
    // and the new template renders empty / wrong-sport fields.
    setDemoSport(newSportId)
    setDemoPayloadVersion((v) => v + 1)
  }, [])

  // Callback for when demo display state changes
  const handleDemoDisplayStateChange = useCallback((newDisplayState: DISPLAY_STATE) => {
    setDemoDisplayStateOverride(newDisplayState)
  }, [])

  // Callback for when demo scorebug position changes
  const handleDemoScoreBugPositionChange = useCallback((position: ScoreBugPosition) => {
    setDemoScoreBugPosition(position)
  }, [])

  // Callback for when demo banner visibility changes
  const handleDemoGfxStateChange = useCallback((gfxState: GfxState) => {
    setDemoGfxState(gfxState)
  }, [])

  // Fetch S3 data for displayState and sportId with 5-second polling
  useEffect(() => {
    const loadS3Data = async () => {
      try {
        // Only show loading state on initial load
        if (isInitialLoad) {
          setS3Loading(true)
        }
        const data = await fetchMatchScore(customerId, eventGuid)
        setS3Data(data)
        setS3Error(null)
      } catch (error) {
        console.error('Error loading S3 data:', error)
        setS3Error(error instanceof Error ? error.message : 'Failed to load S3 data')
      } finally {
        if (isInitialLoad) {
          setS3Loading(false)
          setIsInitialLoad(false)
        }
      }
    }

    // Initial load
    loadS3Data()

    // Set up 5-second polling (or immediate refetch in demo mode when payload changes)
    const interval = setInterval(loadS3Data, 5000)

    // Cleanup interval on unmount
    return () => clearInterval(interval)
  }, [customerId, eventGuid, isInitialLoad, demoPayloadVersion])

  // Parse displayState (handles both legacy flat and new object format)
  const parsedDisplayState = parseDisplayState(s3Data)

  // Use S3 data for displayState and sportId, fallback to API data
  // In demo mode, default to INTRO (ignoring payload displayState) unless user overrides via DemoController
  const targetDisplayState = isDemoMode
    ? (demoDisplayStateOverride ?? DISPLAY_STATE.INTRO)
    : (parsedDisplayState.element as DISPLAY_STATE) || DISPLAY_STATE.INTRO

  // Scorebug position from displayState, with demo override
  const scoreBugPosition: ScoreBugPosition = demoScoreBugPosition ?? parsedDisplayState.position
  // In demo mode, use the sport override (from URL param or DemoController selection)
  const sportId =
    isDemoMode && demoSportOverride !== undefined ? demoSportOverride : s3Data?.sportId || matchData?.jsonData?.sportId

  // Placeholder gfxConfig for demo mode — replaced by real API data in production
  const demoGfxConfig: GfxConfig = {
    imageSlots: [
      { slotID: GFX_SLOT_ID.HORIZONTAL_BANNER, url: '/assets/demo-sponsor-horizontal.png' },
      { slotID: GFX_SLOT_ID.VERTICAL_BANNER, url: '/assets/demo-sponsor-vertical.png' },
    ],
  }

  // matchData here is CombinedMatchData (extends MatchConfig), which carries gfxConfig
  const effectiveGfxConfig = isDemoMode ? demoGfxConfig : matchData?.gfxConfig
  const effectiveGfxState = isDemoMode ? demoGfxState : s3Data?.gfxState

  // Use animated transition for display state changes
  // This hook must be called before any early returns
  const { displayValue: displayState, componentVisible } = useAnimatedTransition({
    targetValue: targetDisplayState,
    isVisible: true,
  })

  // Measure the rendered scorebug panel height so the centred horizontal banner
  // can be positioned above it. Re-runs whenever sport, displayState, or position
  // changes, since any of those can swap the panel element in the DOM.
  const [scorebugPanelHeight, setScorebugPanelHeight] = useState(0)
  const scorebugRafRef = useRef<number | null>(null)
  const scorebugRetryRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scorebugObserverRef = useRef<ResizeObserver | null>(null)
  useEffect(() => {
    const selector =
      displayState === DISPLAY_STATE.SCOREBAR
        ? '[data-scorebar-panel]'
        : displayState === DISPLAY_STATE.INTRO
          ? '[data-intro-panel]'
          : '[data-scorebug-panel]'

    const attach = () => {
      scorebugObserverRef.current?.disconnect()
      scorebugObserverRef.current = null

      const el = document.querySelector(selector)
      if (!el) {
        // Panel not in DOM yet — retry once after a short delay
        scorebugRetryRef.current = setTimeout(() => {
          const retryEl = document.querySelector(selector)
          if (retryEl) {
            scorebugObserverRef.current = new ResizeObserver(([entry]) =>
              setScorebugPanelHeight(entry.contentRect.height)
            )
            scorebugObserverRef.current.observe(retryEl)
          } else {
            setScorebugPanelHeight(0)
          }
        }, 150)
        return
      }

      scorebugObserverRef.current = new ResizeObserver(([entry]) => setScorebugPanelHeight(entry.contentRect.height))
      scorebugObserverRef.current.observe(el)
    }

    // Defer one frame so the new panel has had a chance to paint
    scorebugRafRef.current = requestAnimationFrame(attach)

    return () => {
      if (scorebugRafRef.current !== null) {
        cancelAnimationFrame(scorebugRafRef.current)
        scorebugRafRef.current = null
      }
      if (scorebugRetryRef.current !== null) {
        clearTimeout(scorebugRetryRef.current)
        scorebugRetryRef.current = null
      }
      scorebugObserverRef.current?.disconnect()
      scorebugObserverRef.current = null
    }
  }, [sportId, displayState, scoreBugPosition])

  // Early returns for loading and error states
  if (isLoading || s3Loading) return
  if (error || s3Error) {
    return (
      <FrameContainer backgroundColor={backgroundColor} resOverride={resOverride}>
        <p className="match-message">Error: {error instanceof Error ? error.message : s3Error || 'Unknown error'}</p>
      </FrameContainer>
    )
  }

  // Render the appropriate template based on display state
  const renderTemplate = () => {
    // Show IntroTemplate for INTRO displayState
    if (displayState === DISPLAY_STATE.INTRO) {
      return (
        <IntroTemplate
          matchData={matchData || {}}
          customerId={customerId}
          eventGuid={eventGuid}
          jsonData={s3Data}
          isVisible={componentVisible}
          sportId={sportId}
        />
      )
    }

    // Show sport-specific template for SCOREBUG/SCOREBAR displayStates
    if (sportId !== undefined) {
      const TemplateComponent = resolveTemplate(sportId)

      if (!TemplateComponent) {
        return <p className="match-message">Unsupported sport ID: {sportId}</p>
      }

      return (
        <TemplateComponent
          matchData={matchData || {}}
          customerId={customerId}
          eventGuid={eventGuid}
          jsonData={s3Data}
          displayState={displayState}
          scoreBugPosition={scoreBugPosition}
          isVisible={componentVisible}
        />
      )
    }

    // No sportId yet
    if (isDemoMode) {
      return <p className="match-message">Select a sport to begin demo</p>
    }

    return null
  }

  return (
    <>
      {isDemoMode && (
        <DemoController
          initialSport={demoSportOverride}
          onPayloadChange={handleDemoPayloadChange}
          onSportChange={handleDemoSportChange}
          onDisplayStateChange={handleDemoDisplayStateChange}
          onScoreBugPositionChange={handleDemoScoreBugPositionChange}
          gfxConfig={demoGfxConfig}
          onGfxStateChange={handleDemoGfxStateChange}
        />
      )}
      <FrameContainer backgroundColor={backgroundColor} resOverride={resOverride}>
        {renderTemplate()}
        <SponsorBanners
          gfxConfig={effectiveGfxConfig}
          gfxState={effectiveGfxState}
          scoreBugPosition={scoreBugPosition}
          scorebugPanelHeight={scorebugPanelHeight}
          displayState={displayState}
        />
      </FrameContainer>
    </>
  )
}

export default Match
